<template>
    <div class="score-board">
        <section class="score-board-section left">
            <router-link :to="redirect">
                <button class="btn btn-textual back-btn">
                    <fa-icon class="icon" icon="arrow-left" />
                    <span class="text">Back</span>
                </button>
            </router-link>
            <div class="active-player">
                <player-label
                    :player="activePlayer"
                    :turnLabel="true"
                    :theme="gameState.theme"
                />
            </div>
        </section>
        <section class="score-board-section middle scoring">
            <div class="team-score white-team">
                <span class="score">{{ team('white').controlledBases.length }} / {{team('white').winningScore || winningScore }}</span>
                <royal-coin color="white" :theme="gameState.theme" />
            </div>
            <div class="score-separator">
                <img class="wc-lock" src="../../assets/wc_lock.png" v-if="!replayControls" />
                <LockCompact class="wc-lock-compact" v-if="!replayControls" />
                <replay-controls v-if="replayControls" />
            </div>
            <div class="team-score black-team">
                <royal-coin color="black" :theme="gameState.theme" />
                <span class="score">{{ team('black').controlledBases.length }} / {{team('black').winningScore || winningScore }}</span>
            </div>
        </section>
        <section class="score-board-section right">
            <mini-direct-messages v-if="!replayControls" />
            <div class="icon-wrapper forfeit-wrapper tooltip-trigger" v-if="!isSpectator && !replayControls">
                <span class="btn-forfeit" @click="onForfeitGame(gameId)">
                    <fa-icon class="forfeit-icon" icon="flag" />
                </span>
                <tooltip value="Forfeit" />
            </div>
            <div class="icon-wrapper rules-wrapper tooltip-trigger" v-if="!replayControls">
                <span class="btn-rules" @click="showRulesModal">
                    <fa-icon class="rules-icon" icon="scroll" />
                </span>
                <tooltip value="Rules" />
            </div>
            <div class="move-count-wrapper" v-if="replayControls">
                <div class="move-count">Move: {{currentMove}}/{{totalMoves}}</div>
            </div>
            <div class="share-replay-wrapper tooltip-trigger" v-if="replayControls">
                <span class="btn-share" @click="showShareModal">
                    <fa-icon class="share-icon" icon="share" />
                    <span class="share-text">Share</span>
                </span>
                 <tooltip value="Share" />
            </div>
            <div class="initiative" :class="{[initiativeTeam.color]: true}">
                <span class="text">Initiative </span>
                <royal-coin :color="initiativeTeam.color" :theme="gameState.theme" />
            </div>
        </section>
    </div>
</template>

<script>
import LockCompact from '../../assets/lock_green.svg';

export default {
    name: 'ScoreBoard',
    components: {
        LockCompact,
    },
    props: [
        'gameState',
        'replayControls',
        'currentMove',
        'totalMoves',
    ],
    created() {
        let { redirect } = this.$route.params;

        redirect = typeof redirect === 'string' ?
            redirect.replace(/%2F/g, '/').replace(/^([^/])/, '/$1')
            : '/rooms';

        if (this.$route.name === 'replay' ||
            this.defaultRedirect === '/matches') {
            this.redirect = '/matches';
        }

        if (redirect) {

            if (redirect.indexOf('/matches/') !== -1) {
                redirect = `/matches/${encodeURIComponent(redirect.split('/matches/')[1])}`;
            }

            this.redirect = redirect;
        }
    },
    data() {
        return {
            redirect: '/rooms',
        };
    },
    computed: {
        defaultRedirect() {
            const { redirect } = this.$route.params;
            return redirect ? String(redirect).toLowerCase() : null;
        },
        initiativeTeam() {
            return this.players.filter(player => player.hasInitiative)[0].team;
        },
        players() {
            return this.gameState.players;
        },
        teams() {
            return this.gameState.teams;
        },
        activePlayer() {
            return this.players.filter(player => player.id === this.gameState.activePlayerId)[0];
        },
        winningScore() {
            return this.gameState.winningScore;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        username() {
            return this.$store.state.loginState.username;
        },
    },
    methods: {
        team(color) {
            return this.teams.filter(team => team.color === color)[0];
        },
        onForfeitGame(gameId) {
            this.$store.dispatch('openModal', { type: 'leaveGame', data: { gameId } });
        },
        showRulesModal() {
            this.$store.dispatch('openModal', { type: 'rules' });
        },
        showShareModal() {
            this.$store.dispatch('openModal', { type: 'share' });
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .score-board {
        background: $cream;
        box-shadow: 0px 1px 5px 0px $light-medium-gray;
        display: flex;
        justify-content: space-between;
        padding: 2px 20px;
        z-index: 15;

        > * {
            align-self: center;
        }

        .active-player {
            vertical-align: sub;

            .player-label {
                padding: 3px 4px;
            }
        }

        .text {
            line-height: 1em;
        }

        .player-label {

            .text {
                line-height: initial;
            }
        }

        .back-btn {
            margin-right: 10px;
            font-weight: 300;

            .icon {
                font-weight: 300;
            }
        }

        .score-board-section {
            display: flex;
            flex: 1 1 0;

            > * {
                align-self: center;
            }

            &.left {
                text-align: center;
            }

            &.left {
                text-align: left;
            }

            &.right {
                justify-content: flex-end;
                text-align: right;

                > * {
                    margin-left: 7.5px;
                }
            }

            .text {
                display: none;
            }
        }

        .initiative {
            display: flex;
            position: relative;
            text-align: right;

            > * {
                align-self: center;
            }

            .text {
                font-size: .9em;
                margin-right: 5px;
            }

            .royal-coin {
                vertical-align: middle;
                height: 35px;
                width: 35px;
            }
        }

        .scoring {
            justify-content: center;

            .team-score {
                display: flex;
                font-family: 'Livingstone';
                font-size: 1em;

                .score {
                    white-space: nowrap;
                }

                > * {
                    align-self: center;
                }
            }

            .royal-coin {
                height: 25px;
                width: 25px;
                vertical-align: middle;
                display: inline-block;
                margin: auto 8px;
            }
        }

        .score-separator {
            display: inline-block;
            margin: auto 10px;
            vertical-align: middle;

            .wc-lock {
                height: auto;
                max-height: 40px;
                vertical-align: middle;
                width: auto;
                display: none;
            }

            .wc-lock-compact {
                height: 40px;
                width: auto;
                vertical-align: middle;
                display: inline-block;
            }
        }

        .icon-wrapper {
            display: none;
            cursor: pointer;
            position: relative;

            > * {
                align-self: center;
            }
        }

        .share-replay-wrapper {
            cursor: pointer;
            margin-right: 5px;
            vertical-align: middle;

            .btn-share {
                text-align: left;
            }

            .share-icon {
                padding: 4px 6px 4px 0;
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
            }

            .share-text {
                width: 0;
                overflow: hidden;
                display: inline-block;
                vertical-align: middle;
            }
        }

        .move-count-wrapper {
            margin-right: 5px;
            border-radius: 4px;
            min-width: 100px;
            text-align: center;

            .move-count {
                font-family: 'LivingStone', 'RobotoSlab', 'Avenir','Arial';
                vertical-align: middle;
            }
        }
    }

    .replay-game-container {

        .score-board {

            .initiative {

                .text {
                    display: none;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-xs) {

        .score-board {

            .icon-wrapper {
                display: flex;
            }
        }

    }

    @media only screen and (min-width: $screen-sm) {

        .score-board {
            flex-direction: row;

            .active-player {

                .player-label {
                    padding: 4px 6px;
                }
            }

            .initiative {

                .royal-coin {
                    height: 40px;
                    width: 40px;
                }
            }

            .score-board-section {

                &.right {

                    > * {
                        margin-left: 15px;

                    &.direct-messages-wrapper {
                            margin-left: 0;
                        }
                    }
                }

                .text {
                    display: initial;
                }
            }

            .scoring {
                min-width: 375px;

                .team-score {
                    flex-direction: row;
                    font-size: 1.3em;

                    &.white-team {

                        .royal-coin {
                            order: 1;
                        }

                        .score {
                            order: 0;
                        }
                    }
                }

                .royal-coin {
                    vertical-align: middle;
                    height: 40px;
                    width: 40px;
                }
            }

            .score-separator  {

                .wc-lock {
                    display: inline-block;
                }

                .wc-lock-compact {
                    display: none;
                }
            }

            .share-replay-wrapper {

                &:hover {

                    .share-text {
                        animation-name: reveal-medium;
                    }
                }
            }
        }

        .game-container {

            &.replay {

                .scoring {
                    min-width: 475px;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-md) {

        .score-board {

            .initiative {

                .text {
                    font-size: 1em;
                }
            }
        }

        .replay-game-container {

            .score-board {

                .initiative {

                    .text {
                        display: block;
                    }
                }
            }
        }
    }

</style>
